import { Package } from 'lucide-react';
import { lazy } from 'react';
import { msg } from '@/lib/i18n';
import { type EventNotice, registerInvalidation } from '@/lib/realtime';
import { registerEntityHref } from '@/ui';
import { registerEntityTab } from '../entityTabs';
import { defineModule } from '../types';
import { assetRoute, assetsRoute, beaconsRoute, newPlaceRoute, newRuleRoute, placeRoute, placesRoute, ruleRoute, rulesRoute, visitsRoute } from './routes';

const visitKeys = (n: EventNotice) =>
  n.type.startsWith('places.visit')
    ? [
        ['places', 'visits'],
        ['places', 'summary'],
        ['places', 'present'],
        ['places', 'places'],
      ]
    : [];

/**
 * Places & assets (server module `places`): places and geofences with visits (under Live), and
 * asset tracking — assets, beacons and asset rules (under Manage, next to Fleet).
 */
export const placesModule = defineModule({
  key: 'places',
  routes: [placesRoute, newPlaceRoute, placeRoute, visitsRoute, assetsRoute, assetRoute, beaconsRoute, rulesRoute, newRuleRoute, ruleRoute],
  nav: [
    {
      key: 'assets',
      label: msg('Assets'),
      description: msg('Trailers, containers and machines: where they are, how they are used'),
      icon: Package,
      to: '/fleet/assets',
      section: 'manage',
      order: 82,
      module: 'places',
      permission: ['places.asset.read', 'places.beacon.read', 'places.rule.read'],
      match: ['/fleet/assets', '/assets', '/fleet/beacons', '/fleet/asset-rules'],
      children: [
        { key: 'assets', label: msg('Assets'), to: '/fleet/assets', permission: 'places.asset.read', match: ['/fleet/assets', '/assets'] },
        { key: 'beacons', label: msg('Beacons'), to: '/fleet/beacons', permission: 'places.beacon.read' },
        { key: 'rules', label: msg('Asset rules'), to: '/fleet/asset-rules', permission: 'places.rule.read' },
      ],
    },
  ],
  setup() {
    registerEntityHref('place', (id) => `/places/${id}`);
    registerEntityHref('asset', (id) => `/assets/${id}`);
    registerEntityHref('beacon', (id) => `/fleet/beacons?selected=${id}`);
    registerEntityHref('asset_rule', (id) => `/fleet/asset-rules/${id}`);

    // Place page (IA §3): Map · Visits · Rules, plus Details for editing.
    registerEntityTab('place', { key: 'map', label: msg('Map'), order: 10, component: lazy(() => import('./places/tabs/PlaceMapTab')) });
    registerEntityTab('place', {
      key: 'visits',
      label: msg('Visits'),
      order: 20,
      permission: 'places.visit.read',
      component: lazy(() => import('./places/tabs/PlaceVisitsTab')),
    });
    registerEntityTab('place', {
      key: 'rules',
      label: msg('Rules'),
      order: 30,
      permission: 'places.rule.read',
      component: lazy(() => import('./places/tabs/PlaceRulesTab')),
    });
    registerEntityTab('place', {
      key: 'details',
      label: msg('Details'),
      order: 40,
      permission: 'places.place.manage',
      component: lazy(() => import('./places/tabs/PlaceDetailsTab')),
    });

    // Asset page (IA §3): Overview · Activity · Details, plus Usage, Rules & alerts, Places.
    registerEntityTab('asset', { key: 'overview', label: msg('Overview'), order: 10, component: lazy(() => import('./assets/tabs/AssetOverviewTab')) });
    registerEntityTab('asset', { key: 'activity', label: msg('Activity'), order: 20, component: lazy(() => import('./assets/tabs/AssetActivityTab')) });
    registerEntityTab('asset', { key: 'usage', label: msg('Usage'), order: 30, component: lazy(() => import('./assets/tabs/AssetUsageTab')) });
    registerEntityTab('asset', { key: 'details', label: msg('Details'), order: 40, component: lazy(() => import('./assets/tabs/AssetDetailsTab')) });
    registerEntityTab('asset', {
      key: 'rules',
      label: msg('Rules & alerts'),
      order: 50,
      permission: 'places.rule.read',
      component: lazy(() => import('./assets/tabs/AssetRulesTab')),
    });
    registerEntityTab('asset', {
      key: 'places',
      label: msg('Places'),
      order: 60,
      permission: 'places.visit.read',
      component: lazy(() => import('./assets/tabs/AssetPlacesTab')),
    });

    // Vehicle › Places visited (IA §3).
    registerEntityTab('vehicle', {
      key: 'places',
      label: msg('Places visited'),
      order: 70,
      permission: 'places.visit.read',
      module: 'places',
      component: lazy(() => import('./visits/VehicleVisitsTab')),
    });

    registerInvalidation('place', (n) => [
      ['places', 'places'],
      ['places', 'place', n.subject_id ?? ''],
      ['places', 'rules'],
    ]);
    registerInvalidation('asset', (n) => [
      ['places', 'assets'],
      ['places', 'asset', n.subject_id ?? ''],
      ['places', 'assets-live'],
      ['places', 'asset-rules-of', n.subject_id ?? ''],
      ...visitKeys(n),
    ]);
    registerInvalidation('vehicle', visitKeys);
    registerInvalidation('beacon', (n) => [
      ['places', 'beacons'],
      ['places', 'beacon', n.subject_id ?? ''],
      ['places', 'assets'],
    ]);
    registerInvalidation('asset_rule', (n) => [
      ['places', 'rules'],
      ['places', 'rule', n.subject_id ?? ''],
      ['places', 'asset-rules-of'],
    ]);
  },
});
